import { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Loader2, Sparkles, CheckCircle2, AlertCircle, Database, X } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { useAuth } from '@/lib/auth';
import { toast } from 'sonner';

/**
 * Handles the redirect-fragment from Emergent Google Auth.
 * Flow:
 *   1. On mount, parse `#session_id=...` from window.location.hash
 *   2. Exchange the session_id with the backend (sets JWT + rb_session cookie)
 *   3. Strip the fragment from the URL
 *   4. If `is_new_user`, prompt the user to import demo data (one-shot)
 *   5. Navigate to /dashboard
 *
 * This component renders at the URL the redirect lands on (we use
 * `/auth/callback`) and the user only sees it for ~1 second.
 */
export default function AuthCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const { googleLogin, migrateDemo, setSession } = useAuth();

  const ranRef = useRef(false);
  const [status, setStatus] = useState('exchanging'); // 'exchanging' | 'migrate-prompt' | 'migrating' | 'done' | 'error'
  const [errorMsg, setErrorMsg] = useState(null);
  const [userInfo, setUserInfo] = useState(null);
  const [migrateOpen, setMigrateOpen] = useState(false);

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;

    (async () => {
      try {
        const hash = window.location.hash || '';
        const search = window.location.search || '';
        const hashParams = new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash);
        const searchParams = new URLSearchParams(search);
        const sessionId = hashParams.get('session_id');
        // Phase 10.4 — native Google OAuth lands here with `?token=...` (a
        // freshly minted JWT). The provider is reported in the hash so we
        // can pick the right exchange path.
        const nativeToken = searchParams.get('token');
        const provider = hashParams.get('provider');

        if (nativeToken) {
          // Native flow: token already minted by /api/auth/google-native/callback.
          // Persist it via the same code paths we use for password login.
          const { setToken } = await import('@/lib/api');
          setToken(nativeToken);
          // Strip the token from the URL so a refresh doesn't keep it visible.
          window.history.replaceState(null, '', window.location.pathname);
          // Fetch the user so the auth context populates correctly.
          const { Auth } = await import('@/lib/api');
          const u = await Auth.me();
          // Phase 16 — push the user into the AuthProvider state ATOMICALLY
          // so the next page mount sees `user` populated. Without this, the
          // first dashboard mount post-redirect found AuthProvider with
          // `user=null, loading=false`, which used to trip the consumer
          // pages' `demoLogin()` and overwrite the Google token. The
          // hardened demoLogin guard already refuses to overwrite any
          // existing token, but updating context state here keeps the
          // entire app in sync the moment we redirect.
          setSession(nativeToken, u);
          setUserInfo(u);
          setStatus('done');
          // Phase 16 — pick the right copy based on the intent the user
          // chose on Login vs Signup. Falls back to "Signed in" if the
          // breadcrumb is missing (e.g., direct callback hit).
          const intent = (() => {
            try { return sessionStorage.getItem('rb-google-intent'); } catch (_) { return null; }
          })();
          try { sessionStorage.removeItem('rb-google-intent'); } catch (_) { /* ignore */ }
          if (intent === 'signup') {
            toast.success(`Welcome to Rewards Brain, ${u?.name || 'there'}!`);
          } else {
            toast.success(`Signed in as ${u?.name || 'you'}`);
          }
          window.dispatchEvent(new CustomEvent('rb:user-changed', { detail: u }));
          setTimeout(() => navigate(u?.is_admin ? '/admin' : '/dashboard', { replace: true }), 350);
          return;
        }

        if (!sessionId) {
          setStatus('error');
          setErrorMsg('No session info in the URL. Please try signing in again.');
          return;
        }

        // Legacy Emergent-proxy flow (kept as a fallback).
        // Clean the fragment so a refresh doesn't re-fire the exchange.
        window.history.replaceState(null, '', window.location.pathname + window.location.search);

        const res = await googleLogin(sessionId);
        setUserInfo(res?.user);
        const isNew = !!res?.is_new_user;
        if (isNew) {
          setStatus('migrate-prompt');
          setMigrateOpen(true);
        } else {
          setStatus('done');
          toast.success(`Signed in as ${res?.user?.name || 'you'}`);
          setTimeout(() => navigate('/dashboard', { replace: true }), 350);
        }
        // Suppress unused-var lint for the `provider` we parsed for diagnostics.
        void provider;
      } catch (e) {
        setStatus('error');
        setErrorMsg(e?.message || 'Authentication failed');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const doMigrate = async () => {
    setStatus('migrating');
    try {
      const r = await migrateDemo();
      toast.success(`Imported ${r.cards_copied} card${r.cards_copied === 1 ? '' : 's'} and ${r.transactions_copied} transaction${r.transactions_copied === 1 ? '' : 's'} from the demo.`);
      setMigrateOpen(false);
      navigate('/dashboard', { replace: true });
    } catch (e) {
      toast.error(e?.message || 'Could not import demo data');
      setStatus('migrate-prompt');
    }
  };

  const skipMigrate = () => {
    setMigrateOpen(false);
    toast.message('Continue with a fresh portfolio.');
    navigate('/cards', { replace: true });
  };

  return (
    <div className="min-h-screen grid place-items-center bg-[hsl(var(--background))] p-6">
      <Card className="w-full max-w-md p-8 text-center" data-testid="auth-callback-card">
        {status === 'exchanging' && (
          <>
            <div className="mx-auto h-12 w-12 rounded-full bg-primary/10 grid place-items-center mb-4">
              <Loader2 className="h-5 w-5 animate-spin text-primary" />
            </div>
            <h2 className="font-display text-lg font-semibold">Signing you in…</h2>
            <p className="mt-1 text-xs text-muted-foreground">Securing your session, give us a moment.</p>
          </>
        )}
        {status === 'done' && (
          <>
            <div className="mx-auto h-12 w-12 rounded-full bg-emerald-500/10 grid place-items-center mb-4">
              <CheckCircle2 className="h-5 w-5 text-emerald-500" />
            </div>
            <h2 className="font-display text-lg font-semibold">You&apos;re in</h2>
            <p className="mt-1 text-xs text-muted-foreground">Redirecting to your dashboard…</p>
          </>
        )}
        {status === 'migrating' && (
          <>
            <div className="mx-auto h-12 w-12 rounded-full bg-primary/10 grid place-items-center mb-4">
              <Loader2 className="h-5 w-5 animate-spin text-primary" />
            </div>
            <h2 className="font-display text-lg font-semibold">Importing demo data…</h2>
            <p className="mt-1 text-xs text-muted-foreground">Copying cards, transactions, and leakage events.</p>
          </>
        )}
        {status === 'migrate-prompt' && (
          <>
            <div className="mx-auto h-12 w-12 rounded-full bg-primary/10 grid place-items-center mb-4">
              <Sparkles className="h-5 w-5 text-primary" />
            </div>
            <h2 className="font-display text-lg font-semibold">Welcome, {userInfo?.name?.split(' ')[0] || 'there'}!</h2>
            <p className="mt-1 text-xs text-muted-foreground">Choose how you&apos;d like to start.</p>
            <Button className="mt-5 w-full" onClick={() => setMigrateOpen(true)} data-testid="auth-callback-show-migrate">Start setup</Button>
          </>
        )}
        {status === 'error' && (
          <>
            <div className="mx-auto h-12 w-12 rounded-full bg-rose-500/10 grid place-items-center mb-4">
              <AlertCircle className="h-5 w-5 text-rose-500" />
            </div>
            <h2 className="font-display text-lg font-semibold">Sign-in failed</h2>
            <p className="mt-1 text-xs text-muted-foreground" data-testid="auth-callback-error">{errorMsg}</p>
            <Button variant="outline" className="mt-5 w-full" onClick={() => navigate('/login', { replace: true })}>Back to sign-in</Button>
          </>
        )}
      </Card>

      <Dialog open={migrateOpen} onOpenChange={setMigrateOpen}>
        <DialogContent className="sm:max-w-md" data-testid="auth-migrate-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <Database className="h-4 w-4 text-primary" /> Import demo data?
            </DialogTitle>
            <DialogDescription className="text-xs leading-relaxed">
              Copy the curated demo portfolio (6 cards across HSBC, Axis, HDFC, ICICI, IndusInd, SBI plus
              sample transactions and leakage events) into your personal account so you can try the
              recommender immediately. You can clear it anytime from <b>My Cards</b>.
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-lg border border-border bg-[hsl(var(--surface-2))] p-3 text-xs text-muted-foreground">
            <div className="flex items-center gap-2"><CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" /> Cards, transactions, and milestone progress are duplicated &mdash; not shared.</div>
            <div className="mt-1.5 flex items-center gap-2"><CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" /> Demo account is untouched.</div>
          </div>
          <DialogFooter className="gap-2 flex-row">
            <Button variant="outline" onClick={skipMigrate} className="gap-1.5" data-testid="auth-migrate-skip">
              <X className="h-3.5 w-3.5" /> Start fresh
            </Button>
            <Button onClick={doMigrate} className="gap-1.5" data-testid="auth-migrate-confirm">
              <Database className="h-3.5 w-3.5" /> Import demo
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
